import { useEffect, useState } from "react";
import type { DomainRow, UserRow, DomainCategory } from "./broadcastNotify";
import { companyFromDomain, initials, avatarColorClass } from "./format";

/**
 * The drag-and-drop sorting board — the primary way an admin turns a pile
 * of unknown email domains into the four categories the sidebar groups by.
 *
 * "External" is not a stored state: it's `category = null`, which every
 * domain starts at the first time anyone from it signs in (see
 * registerDomainIfNew). So the left column is a queue that *drains* —
 * sort a domain and it leaves External for good.
 *
 * Dragging is the fast path, but every card also carries a plain "Move…"
 * select. That isn't redundant: HTML5 drag-and-drop does nothing on touch
 * devices and is invisible to a keyboard, so the select is what makes the
 * board usable on an iPad or without a mouse.
 */

type Bucket = { key: DomainCategory | null; label: string; hint: string };

const BUCKETS: Bucket[] = [
  { key: null, label: "External", hint: "Unsorted — everyone starts here" },
  { key: "team", label: "Team", hint: "Internal staff" },
  { key: "supplier", label: "Suppliers", hint: "We buy from them" },
  { key: "customer", label: "Customers", hint: "We sell to them" },
  { key: "partner", label: "Partners", hint: "Buy from and sell to" },
];

/** null isn't usable as a DOM id or a <select> value — "external" is. */
const bucketId = (k: DomainCategory | null) => k ?? "external";
const fromBucketId = (id: string): DomainCategory | null =>
  id === "external" ? null : (id as DomainCategory);

export type OrganiseProps = {
  domains: DomainRow[] | null;
  users: UserRow[];
  enforced: boolean;
  busyDomain: string | null;
  busyEmail: string | null;
  error: string;
  query: string;
  onQueryChange: (q: string) => void;
  newDomain: string;
  onNewDomainChange: (v: string) => void;
  onAddDomain: () => void;
  onMove: (domain: string, category: DomainCategory | null) => void;
  onSetCompany: (domain: string, company: string | null) => void;
  onSetVisibleToExternal: (domain: string, visible: boolean) => void;
  onMoveUser: (email: string, category: DomainCategory | null) => void;
};

export default function AdminOrganise({
  domains,
  users,
  enforced,
  busyDomain,
  busyEmail,
  error,
  query,
  onQueryChange,
  newDomain,
  onNewDomainChange,
  onAddDomain,
  onMove,
  onSetCompany,
  onSetVisibleToExternal,
  onMoveUser,
}: OrganiseProps) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [overBucket, setOverBucket] = useState<string | null>(null);
  /* Sorting whole domains is the job 95% of the time — one drag covers
   * everyone at that company. People mode is the exception handler: the
   * contractor on a gmail address, or the one person at a supplier who
   * should be treated as a partner. */
  const [mode, setMode] = useState<"domains" | "people">("domains");

  if (domains === null) return <div className="admin-fs__empty">Loading…</div>;

  const q = query.trim().toLowerCase();
  const matches = (d: DomainRow) =>
    !q || d.domain.includes(q) || (d.company || "").toLowerCase().includes(q);
  const matchesUser = (u: UserRow) =>
    !q ||
    u.name.toLowerCase().includes(q) ||
    u.email.includes(q) ||
    (u.company || "").toLowerCase().includes(q);

  const unsorted = domains.filter((d) => !d.category);
  const pendingPeople = unsorted.reduce((sum, d) => sum + d.people, 0);
  const overrideCount = users.filter((u) => u.override).length;
  const hiddenByQuery =
    mode === "domains"
      ? domains.length - domains.filter(matches).length
      : users.length - users.filter(matchesUser).length;

  const drop = (bucket: Bucket) => (e: React.DragEvent) => {
    e.preventDefault();
    setOverBucket(null);
    const id = e.dataTransfer.getData("text/plain") || dragging;
    setDragging(null);
    if (!id) return;
    if (mode === "people") {
      const user = users.find((u) => u.email === id);
      if (!user || (user.category ?? null) === bucket.key) return;
      onMoveUser(id, bucket.key);
      return;
    }
    const row = domains.find((d) => d.domain === id);
    // Dropping a card back where it came from is a no-op, not a save —
    // otherwise every mis-drop costs a pointless round trip.
    if (!row || (row.category ?? null) === bucket.key) return;
    onMove(id, bucket.key);
  };

  return (
    <>
      <div className="admin-fs__titlerow">
        <div>
          <h2 className="admin-fs__h2">Sort companies into categories</h2>
          <p className="admin-fs__sub">
            Drag a domain into a category — or use its <b>Move…</b> menu. Sorting a
            domain covers everyone who signs in with that email address, and the
            change shows up in the team's sidebar straight away.
          </p>
        </div>
        <span className={`admin-fs__state ${enforced ? "on" : ""}`}>
          {enforced ? "Enforcing" : "Open — nothing restricted yet"}
        </span>
      </div>

      <div className="org-summary">
        <div className={`org-summary__stat ${unsorted.length ? "is-pending" : "is-clear"}`}>
          <b>{unsorted.length}</b>
          <span>{unsorted.length === 1 ? "domain" : "domains"} to sort</span>
        </div>
        <div className={`org-summary__stat ${pendingPeople ? "is-pending" : "is-clear"}`}>
          <b>{pendingPeople}</b>
          <span>{pendingPeople === 1 ? "person waiting" : "people waiting"}</span>
        </div>
        <div className="org-summary__stat">
          <b>{domains.length - unsorted.length}</b>
          <span>sorted</span>
        </div>
        {overrideCount > 0 && (
          <div className="org-summary__stat">
            <b>{overrideCount}</b>
            <span>{overrideCount === 1 ? "person overridden" : "people overridden"}</span>
          </div>
        )}
      </div>

      <div className="org-modes" role="tablist">
        <button
          role="tab"
          aria-selected={mode === "domains"}
          className={mode === "domains" ? "on" : ""}
          onClick={() => setMode("domains")}
        >
          Companies
          <span className="org-modes__hint">sort a whole domain at once</span>
        </button>
        <button
          role="tab"
          aria-selected={mode === "people"}
          className={mode === "people" ? "on" : ""}
          onClick={() => setMode("people")}
        >
          People
          <span className="org-modes__hint">override one person</span>
        </button>
      </div>

      <div className="admin-fs__tools">
        <div className="admin-fs__addrow">
          <input
            type="text"
            placeholder="Add a domain before anyone signs in, e.g. example.com.au"
            value={newDomain}
            onChange={(e) => onNewDomainChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onAddDomain();
            }}
          />
          <button
            className="admin-btn"
            disabled={!newDomain.trim() || busyDomain !== null}
            onClick={onAddDomain}
          >
            Add
          </button>
        </div>
        <input
          className="admin-fs__search"
          type="text"
          placeholder="Filter the board by domain or company…"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>

      {error && <div className="admin-note admin-note--error">{error}</div>}

      {domains.length === 0 ? (
        <div className="admin-fs__empty">
          No domains yet — add one above, or they'll appear here as people sign in.
        </div>
      ) : (
        <>
          {q && hiddenByQuery > 0 && (
            <div className="org-filternote">
              Filtered by “{query.trim()}” — {hiddenByQuery} hidden.
            </div>
          )}
          <div className="org-board">
            {BUCKETS.map((bucket) => {
              const cards = domains.filter(
                (d) => (d.category ?? null) === bucket.key && matches(d)
              );
              const people = users.filter(
                (u) => (u.category ?? null) === bucket.key && matchesUser(u)
              );
              const count = mode === "people" ? people.length : cards.length;
              const id = bucketId(bucket.key);
              return (
                <section
                  key={id}
                  className={`org-col org-col--${id} ${overBucket === id ? "is-over" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (overBucket !== id) setOverBucket(id);
                  }}
                  onDragLeave={(e) => {
                    // Only clear when the pointer actually left the column,
                    // not when it crossed onto a child card inside it.
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setOverBucket((cur) => (cur === id ? null : cur));
                    }
                  }}
                  onDrop={drop(bucket)}
                >
                  <header className="org-col__head">
                    <div className="org-col__title">
                      {bucket.label}
                      <span className="org-col__count">{count}</span>
                    </div>
                    <div className="org-col__hint">
                      {mode === "people" && bucket.key === null
                        ? "No override — follows their company"
                        : bucket.hint}
                    </div>
                  </header>
                  <div className="org-col__body">
                    {mode === "people" ? (
                      people.length === 0 ? (
                        <div className="org-col__empty">
                          {bucket.key === null ? "Nobody waiting 🎉" : "Drop a person here"}
                        </div>
                      ) : (
                        people.map((u) => (
                          <PersonCard
                            key={u.email}
                            row={u}
                            busy={busyEmail === u.email}
                            dragging={dragging === u.email}
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", u.email);
                              e.dataTransfer.effectAllowed = "move";
                              setDragging(u.email);
                            }}
                            onDragEnd={() => {
                              setDragging(null);
                              setOverBucket(null);
                            }}
                            onMoveUser={onMoveUser}
                          />
                        ))
                      )
                    ) : cards.length === 0 ? (
                      <div className="org-col__empty">
                        {bucket.key === null ? "Nothing waiting 🎉" : "Drop a domain here"}
                      </div>
                    ) : (
                      cards.map((row) => (
                        <DomainCard
                          key={row.domain}
                          row={row}
                          busy={busyDomain === row.domain}
                          dragging={dragging === row.domain}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/plain", row.domain);
                            e.dataTransfer.effectAllowed = "move";
                            setDragging(row.domain);
                          }}
                          onDragEnd={() => {
                            setDragging(null);
                            setOverBucket(null);
                          }}
                          onMove={onMove}
                          onSetCompany={onSetCompany}
                          onSetVisibleToExternal={onSetVisibleToExternal}
                        />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

/** One person. Unlike a domain card this has no company field — a person
 * belongs to whatever company their domain does; only their category can
 * be overridden here. */
function PersonCard({
  row,
  busy,
  dragging,
  onDragStart,
  onDragEnd,
  onMoveUser,
}: {
  row: UserRow;
  busy: boolean;
  dragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onMoveUser: (email: string, category: DomainCategory | null) => void;
}) {
  const current = bucketId(row.category ?? null);
  const company = row.company || companyFromDomain(row.domain);

  return (
    <article
      className={`org-card org-person ${busy ? "is-busy" : ""} ${dragging ? "is-dragging" : ""}`}
      draggable={!busy}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="org-card__top">
        <span className={`org-person__av ${avatarColorClass(row.uid || row.email)}`}>
          {initials(row.name)}
        </span>
        <span className="org-person__id">
          <span className="org-person__name" title={row.name}>
            {row.name}
          </span>
          <span className="org-person__email" title={row.email}>
            {row.email}
          </span>
        </span>
      </div>

      <div className="org-person__from">
        {company}
        {/* Say plainly that this person no longer follows their domain,
            and what they'd fall back to if the override were cleared. */}
        {row.override && (
          <span className="org-person__badge">
            overridden{row.domainCategory ? ` · domain says ${row.domainCategory}` : ""}
          </span>
        )}
      </div>

      <select
        className="org-card__move"
        aria-label={`Change the category for ${row.name}`}
        value=""
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          e.target.value = "";
          if (!next) return;
          onMoveUser(row.email, next === "__clear" ? null : fromBucketId(next));
        }}
      >
        <option value="">Move…</option>
        {/* External is left out on purpose: for a person it isn't a
            destination, it's the absence of an override. "Clear override"
            below is the same action said properly. */}
        {BUCKETS.filter((b) => b.key !== null && bucketId(b.key) !== current).map((b) => (
          <option key={bucketId(b.key)} value={bucketId(b.key)}>
            {b.label}
          </option>
        ))}
        {row.override && <option value="__clear">Clear override</option>}
      </select>
    </article>
  );
}

function DomainCard({
  row,
  busy,
  dragging,
  onDragStart,
  onDragEnd,
  onMove,
  onSetCompany,
  onSetVisibleToExternal,
}: {
  row: DomainRow;
  busy: boolean;
  dragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onMove: (domain: string, category: DomainCategory | null) => void;
  onSetCompany: (domain: string, company: string | null) => void;
  onSetVisibleToExternal: (domain: string, visible: boolean) => void;
}) {
  const [company, setCompany] = useState(row.company || "");

  // Keep in step when the server echoes back a changed row (e.g. another
  // admin renamed the company while this board was open).
  useEffect(() => {
    setCompany(row.company || "");
  }, [row.company]);

  const current = bucketId(row.category ?? null);

  return (
    <article
      className={`org-card ${busy ? "is-busy" : ""} ${dragging ? "is-dragging" : ""}`}
      draggable={!busy}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="org-card__top">
        <span className="org-card__domain" title={row.domain}>
          @{row.domain}
        </span>
        <span
          className="org-card__count"
          title={
            row.active < row.people
              ? `${row.people} accounts · ${row.active} have signed into chat`
              : `${row.people} ${row.people === 1 ? "account" : "accounts"}`
          }
        >
          {row.active < row.people ? `${row.active}/${row.people}` : row.people}
        </span>
      </div>

      <input
        className="org-card__company"
        // Without this the browser drags the text selection instead of the
        // card, so the input becomes a dead zone you can't drag from.
        draggable={false}
        placeholder="Company name"
        value={company}
        disabled={busy}
        onChange={(e) => setCompany(e.target.value)}
        onBlur={() => {
          if ((row.company || "") !== company.trim()) {
            onSetCompany(row.domain, company.trim() || null);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />

      <select
        className="org-card__move"
        aria-label={`Move ${row.domain} to a different category`}
        value=""
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          e.target.value = "";
          if (next) onMove(row.domain, fromBucketId(next));
        }}
      >
        <option value="">Move…</option>
        {BUCKETS.filter((b) => bucketId(b.key) !== current).map((b) => (
          <option key={bucketId(b.key)} value={bucketId(b.key)}>
            {b.label}
          </option>
        ))}
      </select>

      {/* Only Team domains have this: it's what lets suppliers/customers
          reach DBM while the other internal companies stay hidden. */}
      {row.category === "team" && (
        <label className="org-card__toggle">
          <input
            type="checkbox"
            checked={row.visible_to_external}
            disabled={busy}
            onChange={(e) => onSetVisibleToExternal(row.domain, e.target.checked)}
          />
          <span>Externals can see this</span>
        </label>
      )}
    </article>
  );
}
